import clsx from 'clsx'
import { type MouseEvent, useEffect, useState } from 'react'
import 'swiper/css'
import 'swiper/css/effect-fade'
import { A11y, EffectFade } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery'
import { Modal } from '@/shared/ui'
import { SliderNextButton, SliderPrevButton } from '@/shared/ui/SliderButtons'

import { isReviewVideo } from '../../lib/media'
import { findReviewMediaIndex } from '../../model/review-state'
import { ProductReview, ProductReviewVote, ReviewMediaItem } from '../../model/types'
import { ReviewCard } from '../review-card/ReviewCard'
import { ProductReviewListSkeleton } from '../review-list-skeleton/ProductReviewListSkeleton'

import { GalleryMedia } from './GalleryMedia'
import { useDesktopGalleryWheelZoom } from './useDesktopGalleryWheelZoom'
import { useMediaGalleryReviews } from './useMediaGalleryReviews'
import { useMediaGallerySwiper } from './useMediaGallerySwiper'
import { useMediaGalleryZoom } from './useMediaGalleryZoom'

import styles from './MediaGalleryModal.module.scss'

export const MediaGalleryModal = ({
	items,
	total = items.length,
	initialIndex,
	open,
	onClose,
	onLoadMore,
	hasMore,
	isLoadingMore = false,
	onVote,
	canVoteOptimistically = true,
	votingReviewId: _votingReviewId,
	isVoteDisabled = false,
	loadDetailedReviews = true
}: {
	items: ReviewMediaItem[]
	total?: number
	initialIndex: number
	open: boolean
	onClose: () => void
	onLoadMore?: () => Promise<void> | void
	hasMore?: boolean
	isLoadingMore?: boolean
	onVote: (reviewId: string, vote: ProductReviewVote) => Promise<ProductReview | undefined> | undefined
	canVoteOptimistically?: boolean
	votingReviewId?: string | null
	isVoteDisabled?: boolean
	loadDetailedReviews?: boolean
}) => {
	const isDesktop = useMediaQuery('(min-width: 992px)')
	const canLoadMore = hasMore ?? items.length < total
	const [activeIndex, setActiveIndex] = useState(initialIndex)
	const [isReviewExpanded, setIsReviewExpanded] = useState(false)
	const [isChromeHidden, setIsChromeHidden] = useState(false)
	const { getItemWithLoadedReview, isReviewLoading, voteForLoadedReview } = useMediaGalleryReviews({
		activeIndex,
		canVoteOptimistically,
		items,
		loadDetailedReviews,
		onVote,
		open
	})

	const activeItem = items[activeIndex] ? getItemWithLoadedReview(items[activeIndex]) : null
	const isActiveVideo = activeItem ? isReviewVideo(activeItem.src) : false
	const {
		closeDesktopZoom,
		handleMediaClick,
		handleMediaTouchEnd,
		handleMediaTouchMove,
		handleMediaTouchStart,
		isGalleryZoomed,
		isMobileImageZoomed,
		mobileImageRef,
		openDesktopZoom,
		resetZoom
	} = useMediaGalleryZoom({
		isActiveVideo,
		isDesktop,
		isReviewExpanded,
		setIsChromeHidden,
		setIsReviewExpanded
	})
	const {
		desktopZoomImageRef,
		handleDesktopZoomPointerDown,
		handleDesktopZoomPointerMove,
		handleDesktopZoomPointerUp,
		handleDesktopZoomWheel,
		resetDesktopWheelZoom
	} = useDesktopGalleryWheelZoom({ enabled: isGalleryZoomed })
	const {
		gallerySwiperRef,
		handleNextSlide,
		handlePrevSlide,
		handleSlideChange,
		modalContentRef,
		shouldLoop,
		slideToMediaIndex,
		updateGallerySwiperAfterOpen
	} = useMediaGallerySwiper({
		activeIndex,
		canLoadMore,
		initialIndex,
		isDesktop,
		isGalleryZoomed,
		isMobileImageZoomed,
		isLoadingMore,
		itemsLength: items.length,
		onLoadMore,
		open,
		resetZoom,
		setActiveIndex,
		setIsChromeHidden,
		setIsReviewExpanded,
		total
	})

	useEffect(() => {
		updateGallerySwiperAfterOpen(open)
	}, [open, updateGallerySwiperAfterOpen])

	useEffect(() => {
		if (open) return

		modalContentRef.current?.querySelectorAll('video').forEach(video => {
			video.pause()
			video.currentTime = 0
		})
	}, [modalContentRef, open])

	const openGalleryMedia = (reviewId: string, photoIndex: number, src?: string) => {
		const nextIndex = findReviewMediaIndex(items, reviewId, photoIndex, src)
		if (nextIndex < 0) return

		slideToMediaIndex(nextIndex)
	}

	const toggleReviewSheet = () => {
		setIsChromeHidden(false)
		setIsReviewExpanded(value => !value)
	}

	const closeDesktopGalleryZoom = () => {
		resetDesktopWheelZoom()
		closeDesktopZoom()
	}

	const handleModalBodyClick = (event: MouseEvent<HTMLDivElement>) => {
		if (!isReviewExpanded) return
		if ((event.target as HTMLElement).closest('.' + styles.galleryMobileReviewCard)) return

		setIsReviewExpanded(false)
	}

	const toggleReviewSheetFromCard = (event: MouseEvent<HTMLDivElement>) => {
		if ((event.target as HTMLElement).closest('button, a')) return
		toggleReviewSheet()
	}

	const renderReviewCard = (
		item: ReviewMediaItem,
		options: { hideMedia?: boolean; disableTextCollapse?: boolean } = {}
	) => {
		return (
			<div className={styles.galleryReviewContent}>
				{isReviewLoading(item) ? (
					<ProductReviewListSkeleton count={1} className={styles.galleryReviewSkeleton} showMediaTabs />
				) : (
					<ReviewCard
						className={styles.galleryReviewInner}
						review={item.review}
						onVote={vote => void voteForLoadedReview(item.review.id, vote)}
						isVoteDisabled={isVoteDisabled || item.review.isUserReview}
						hideMedia={options.hideMedia}
						disableTextCollapse={options.disableTextCollapse}
						activeMediaSrc={item.src}
						onOpenMedia={(photoIndex, src) => openGalleryMedia(item.review.id, photoIndex, src)}
					/>
				)}
			</div>
		)
	}

	if (!items.length) return null

	return (
		<Modal
			className={styles.mediaGalleryModalRoot}
			contentClassName={clsx(
				styles.mediaGalleryModal,
				isChromeHidden && styles._galleryChromeHidden,
				isActiveVideo && styles._mediaGalleryModalVideo,
				isMobileImageZoomed && styles._mediaGalleryModalMobileZoomed,
				isGalleryZoomed && styles._mediaGalleryModalZoomed
			)}
			isOpen={open}
			onClose={onClose}>
			<div ref={modalContentRef} className={styles.mediaGalleryContent}>
				<header className={styles.mediaGalleryHeader}>
					<h2>Фото покупателей</h2>
					<span>
						{activeIndex + 1} из {total}
					</span>
				</header>
				{items.length > 1 && (
					<SliderPrevButton
						className={clsx(styles.galleryModalArrow, styles.galleryModalArrowPrev)}
						disabled={!shouldLoop && activeIndex === 0 && !canLoadMore}
						onClick={handlePrevSlide}
						aria-label="Предыдущее медиа"
					/>
				)}

				<div className={styles.galleryModalBody} onClick={handleModalBodyClick}>
					<Swiper
						key={shouldLoop ? 'desktop-loop' : 'default'}
						modules={[A11y, EffectFade]}
						effect="fade"
						fadeEffect={{ crossFade: true }}
						initialSlide={initialIndex}
						loop={shouldLoop}
						slidesPerView={1}
						spaceBetween={0}
						speed={200}
						allowTouchMove={!isMobileImageZoomed}
						observer
						observeParents
						onSwiper={swiper => {
							gallerySwiperRef.current = swiper
						}}
						onSlideChange={handleSlideChange}
						className={styles.galleryModalSwiper}>
						{items.map(item => {
							const itemWithReview = getItemWithLoadedReview(item)
							const isActiveItem = itemWithReview.id === activeItem?.id

							return (
								<SwiperSlide key={item.id}>
									<div className={styles.galleryModalSlide}>
										<GalleryMedia
											item={itemWithReview}
											onClick={handleMediaClick}
											showZoomButton={isDesktop && !isGalleryZoomed}
											onZoomClick={openDesktopZoom}
											isZoomed={isActiveItem && isMobileImageZoomed}
											imageRef={!isDesktop && isActiveItem ? mobileImageRef : undefined}
											onTouchStart={handleMediaTouchStart}
											onTouchMove={handleMediaTouchMove}
											onTouchEnd={handleMediaTouchEnd}
										/>
										<div className={clsx(styles.galleryReviewCard, styles.gallerySlideReviewCard)}>
											{renderReviewCard(itemWithReview)}
										</div>
									</div>
								</SwiperSlide>
							)
						})}
					</Swiper>
					{activeItem && (
						<div
							className={clsx(
								styles.galleryReviewCard,
								styles.galleryMobileReviewCard,
								isReviewExpanded && styles._galleryReviewCardExpanded,
								isChromeHidden && styles._galleryReviewCardHidden
							)}
							onClick={toggleReviewSheetFromCard}>
							{renderReviewCard(activeItem, { hideMedia: true, disableTextCollapse: true })}
						</div>
					)}
				</div>
				{activeItem && isGalleryZoomed && (
					<div className={styles.galleryZoomOverlay}>
						<GalleryMedia
							item={activeItem}
							className={styles.galleryZoomOverlayMedia}
							showZoomButton
							isZoomButtonActive
							isZoomed
							onZoomClick={closeDesktopGalleryZoom}
							imageRef={desktopZoomImageRef}
							onPointerDown={handleDesktopZoomPointerDown}
							onPointerMove={handleDesktopZoomPointerMove}
							onPointerUp={handleDesktopZoomPointerUp}
							onWheel={handleDesktopZoomWheel}
						/>
					</div>
				)}
				{items.length > 1 && (
					<SliderNextButton
						className={clsx(styles.galleryModalArrow, styles.galleryModalArrowNext)}
						disabled={!shouldLoop && activeIndex === items.length - 1}
						onClick={handleNextSlide}
						aria-label="Следующее медиа"
					/>
				)}
				<div className={styles.mediaGalleryCounter}>
					{activeIndex + 1} из {total}
				</div>
			</div>
		</Modal>
	)
}
